import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../../i18n/index.js';
import { useState, type ReactElement } from 'react';

import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

interface DealActionPlanSnippetProps {
  plan: Extract<StructuredMessagePayload, { type: 'deal_action_plan' }>['plan'];
  onCreateTasks?:
    | ((
        actions: Extract<StructuredMessagePayload, { type: 'deal_action_plan' }>['plan']['actions'],
      ) => void)
    | undefined;
  createTasksDisabled?: boolean;
}

const STAGE_LABEL_KEYS: Record<string, string> = {
  New: 'crm.deals.stage.New',
  Contacted: 'crm.deals.stage.Contacted',
  Negotiation: 'crm.deals.stage.Negotiation',
  Won: 'crm.deals.stage.Won',
  Lost: 'crm.deals.stage.Lost',
};

const PRIORITY_VARIANTS: Record<
  'low' | 'medium' | 'high' | 'urgent',
  'outline' | 'info' | 'warning' | 'destructive'
> = {
  low: 'outline',
  medium: 'info',
  high: 'warning',
  urgent: 'destructive',
};

const PRIORITY_CLASSES: Record<'low' | 'medium' | 'high' | 'urgent', string> = {
  low: '!border-[var(--color-border-hover)] !bg-[var(--g-fill)] !text-[var(--ant-color-text)]',
  medium: '!border-[var(--ant-color-info)] !bg-[var(--ant-color-info)] !text-white',
  high: '!border-[var(--ant-color-warning)] !bg-[var(--ant-color-warning)] !text-white',
  urgent: '!border-[var(--ant-color-error)] !bg-[var(--ant-color-error)] !text-white',
};

function formatDate(value: string | null | undefined, locale: string = 'ru'): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

export function DealActionPlanSnippet({
  plan,
  onCreateTasks,
  createTasksDisabled = false,
}: DealActionPlanSnippetProps): ReactElement {
  const { t, locale } = useTranslation();
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set(plan.actions.map((_, index) => index)),
  );
  const stageLabel = t(STAGE_LABEL_KEYS[plan.deal.stage] ?? plan.deal.stage);
  const selectedActions = plan.actions.filter((_, index) => selectedIndices.has(index));

  const toggleAction = (index: number): void => {
    setSelectedIndices((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <Card data-testid="structured-snippet-deal-action-plan" className="min-w-0">
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="break-words">{plan.deal.title}</CardTitle>
          <Badge
            variant="info"
            className="shrink-0 whitespace-nowrap !border-[var(--ant-color-info)] !bg-[var(--ant-color-info)] !text-white"
          >
            {t('crm.chat.action_plan.badge')}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{t('crm.chat.action_plan.stage')}:</span>
          <span className="font-medium text-foreground">{stageLabel}</span>
          {plan.deal.contactName ? <span>· {plan.deal.contactName}</span> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3 rounded-md bg-muted/40 p-3">
          <div>
            <p className="text-xs text-muted-foreground">{t('crm.chat.action_plan.open_tasks')}</p>
            <p className="font-medium">{plan.context.openTaskCount}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('crm.chat.action_plan.notes')}</p>
            <p className="font-medium">{plan.context.recentNoteCount}</p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            {t('crm.chat.action_plan.actions')}
          </p>
          <p className="mb-2 text-xs text-muted-foreground">
            {i18n.t(
              'crm.features.features.ai.chat.components.snippets.dealactionplansnippet.vybrano',
            )}
            <span className="font-medium text-foreground">{selectedActions.length}</span>{' '}
            {i18n.t('crm.features.features.ai.chat.components.snippets.dealactionplansnippet.iz')}
            {plan.actions.length}
          </p>
          <ol className="space-y-3">
            {plan.actions.map((action, index) => (
              <li
                key={`${action.title}-${index}`}
                data-testid={`deal-action-plan-action-${index}`}
                className="rounded-md border border-border/70 p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <label className="flex min-w-0 items-start gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIndices.has(index)}
                      onChange={() => toggleAction(index)}
                      aria-label={`${t('crm.chat.action_plan.select')} ${index + 1}: ${action.title}`}
                      data-testid={`deal-action-plan-select-${index}`}
                      className="mt-1 size-4 shrink-0 accent-amber-600"
                    />
                    <span className="font-medium">{action.title}</span>
                  </label>
                  <Badge
                    variant={PRIORITY_VARIANTS[action.priority]}
                    className={PRIORITY_CLASSES[action.priority]}
                  >
                    {t(`crm.chat.action_plan.priority.${action.priority}`)}
                  </Badge>
                </div>
                {action.description ? (
                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                    {action.description}
                  </p>
                ) : null}
                {action.rationale ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    <span className="font-medium">{t('crm.chat.action_plan.rationale')}:</span>{' '}
                    {action.rationale}
                  </p>
                ) : null}
                {formatDate(action.dueAt, locale) ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    <span className="font-medium">{t('crm.chat.action_plan.due_at')}:</span>{' '}
                    {formatDate(action.dueAt, locale)}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        </div>

        {onCreateTasks ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="w-full justify-center border border-[var(--color-brand-700)] bg-[var(--color-brand-600)] font-semibold text-white shadow-sm hover:bg-[var(--color-brand-700)]"
            data-testid="deal-action-plan-create-tasks"
            disabled={createTasksDisabled || selectedActions.length === 0}
            onClick={() => onCreateTasks(selectedActions)}
          >
            {t('crm.chat.action_plan.create_tasks')}
          </Button>
        ) : null}

        {onCreateTasks ? (
          <p className="text-xs text-muted-foreground">
            {t('crm.chat.action_plan.manual_confirm')}
          </p>
        ) : null}

        <p className="rounded-md bg-amber-50 p-3 text-xs text-amber-900">
          {t('crm.chat.action_plan.proposal_only')}
        </p>
      </CardContent>
    </Card>
  );
}
